import { useLocalStorage } from "core/hooks/useLocalStorage";
import compare from "just-compare";
import {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
  useMemo,
} from "react";

export const STORAGE_KEY_STATUSES = "RequestDataGridFilters.statuses";
export const STORAGE_KEY_SHOPS = "RequestDataGridFilters.shops";
export const STORAGE_KEY_INSURERS = "RequestDataGridFilters.insurers";
export const STORAGE_KEY_SEARCH = "RequestDataGridFilters.Search";

function getOnChangeFunction(
  stateSetter: Dispatch<SetStateAction<SelectOption[]>>
) {
  return (_: unknown, selectedOptions: SelectOption[]) => {
    stateSetter(selectedOptions);
  };
}

interface SelectOption {
  id?: string;
  value: string;
  label: string;
}
interface LocalStorageState {
  selectedShops: SelectOption[];
  selectedStatuses: SelectOption[];
  selectedInsurers: SelectOption[];
  search: string;
}

const initialState: LocalStorageState = {
  selectedShops: [],
  selectedStatuses: [],
  selectedInsurers: [],
  search: "",
};

export function usePaymentRequestDataGridFilters() {
  const [selectedStatuses, setSelectedStatuses] = useLocalStorage<
    SelectOption[]
  >(STORAGE_KEY_STATUSES, []);
  const [selectedShops, setSelectedShops] = useLocalStorage<SelectOption[]>(
    STORAGE_KEY_SHOPS,
    []
  );
  const [selectedInsurers, setSelectedInsurers] = useLocalStorage<
    SelectOption[]
  >(STORAGE_KEY_INSURERS, []);

  const [search, setSearch] = useLocalStorage<string>(STORAGE_KEY_SEARCH, "");

  const { insurersOnChange, searchOnChange, shopsOnChange, statusesOnChange } =
    useMemo(
      () => ({
        shopsOnChange: getOnChangeFunction(setSelectedShops),
        statusesOnChange: getOnChangeFunction(setSelectedStatuses),
        insurersOnChange: getOnChangeFunction(setSelectedInsurers),
        searchOnChange: (e: ChangeEvent<HTMLInputElement>) => {
          setSearch(e.target.value);
        },
      }),
      [setSelectedShops, setSelectedStatuses, setSelectedInsurers, setSearch]
    );

  function isOptionEqualToValue(option: SelectOption, selected: SelectOption) {
    return option.value === selected.value;
  }

  const reset = useMemo(() => {
    function resetState() {
      setSelectedShops([]);
      setSelectedStatuses([]);
      setSelectedInsurers([]);
      setSearch("");
    }

    const currentState = {
      selectedShops,
      selectedStatuses,
      selectedInsurers,
      search,
    };
    const isBaseState = compare(initialState, currentState);
    return isBaseState ? null : resetState;
  }, [
    selectedShops,
    selectedStatuses,
    selectedInsurers,
    search,
    setSelectedShops,
    setSelectedStatuses,
    setSelectedInsurers,
    setSearch,
  ]);

  return {
    selectedShops,
    selectedStatuses,
    selectedInsurers,
    search,
    insurersOnChange,
    searchOnChange,
    shopsOnChange,
    statusesOnChange,
    isOptionEqualToValue,
    reset,
  };
}
